import { Box } from '@mantine/core';
import { getReferenceString } from '@medplum/core';
import { CSSProperties, ReactElement, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { readPatientCreatedAt } from '@bariendo/lib/patient-created-at';
import { useLiveGraphQL } from '@/hooks/useLiveGraphQL';
import { useMarketOptions } from '@/hooks/useMarketOptions';
import { useCalendarDay, useNow } from '@/hooks/useNow';
import { alert } from '@/services/alert';
import { getRateLimitAlert, isMedplumRateLimitError } from '@/utils/medplumRateLimit';
import { ALL_INQUIRY_PATIENTS_QUERY } from '../../graphql';
import { useCohortTotal } from '../../hooks/useCohortTotal';
import { usePacQueueHydration } from '../../hooks/usePacQueueHydration';
import { usePatientListFilters } from '../../hooks/usePatientListFilters';
import { usePatientNavigatorOptions } from '../../hooks/usePatientNavigatorOptions';
import { COHORT_ROW_CAP, QUEUE_PAGE_SIZE, shouldWarnTruncated } from '../../services/cohortLoading';
import { buildInquiryHubFilter, inquiryHubSubscriptionParams } from '../../services/inquiryFilters';
import { buildInquiryRows, InquiryLeadRow, InquiryPatientResult } from '../../services/inquiryRows';
import { lastCommunicationFloor } from '../../services/pacQueueHydration';
import {
  isQueueFiltered,
  LEAD_STATUS_FILTER_OPTIONS,
  matchesPatientFilterTags,
  optionsFromRows,
  QUEUE_FILTER_ANY,
  QUEUE_FILTERS_OFF,
  QueueFilterKey,
  QueueFilterValues,
  TRACK_FILTER_OPTIONS,
} from '../../services/queueFilters';
import { CohortTruncationNotice } from '../../components/CohortTruncationNotice';
import { ListRangeControl } from '@/components/Hub/ListRangeControl';
import { QueueFilterBar } from '@/components/Hub/QueueFilterBar';
import { QueueFooter } from '@/components/Hub/QueueFooter';
import { QueueTable } from '../../components/QueueTable';
import { lastContactColumn, patientColumn, paymentTrackColumn, pnColumn } from './hubColumns';
import { notesColumn } from './notesColumn';
import { signedUpColumn } from './signedUpColumn';
import { statusColumn } from './statusColumn';
import { EditedRow, useHubRowEdits } from './useHubRowEdits';

/**
 * The Intake hub's Inquiry tab on the shared row (BAR-2808). The legacy
 * `components/InquiryTab` keeps its seven-day window for the flag-off pill
 * (BAR-2853 removes it).
 *
 * Membership, Signed up and the filters run on the server on the sync's Patient
 * tags (BAR-2877). It still loads the whole narrowed set and sorts in the
 * browser, since tags can't sort; paging waits on a sortable signup date (BAR-2888).
 */

const DEFAULT_PRESET = 'all';

const SIGNED_UP_PRESETS = [
  { id: 'all' as const, label: 'All time' },
  { id: 'last-7' as const, label: 'Last 7 days' },
  { id: 'last-30' as const, label: 'Last 30 days' },
];

const DEFAULTS: QueueFilterValues = { ...QUEUE_FILTERS_OFF, status: 'active' };

type HubInquiryRow = EditedRow<InquiryLeadRow>;

const HYDRATION_NEEDS = {
  createdAt: true,
  consult: true,
  callNotes: true,
  careTeam: true,
  eligibility: true,
  stickyNotes: true,
} as const;

const TAB_STYLE: CSSProperties = { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' };

export interface InquiryTabProps {
  /** Injectable for tests; defaults to a clock that keeps ticking. */
  now?: Date;
}

export function InquiryTab({ now }: InquiryTabProps): ReactElement {
  const navigate = useNavigate();
  const resolvedNow = useNow(now);
  const today = useCalendarDay(resolvedNow);

  const filters = usePatientListFilters<InquiryLeadRow>({
    resetKey: 'intake-inquiry',
    chipDefinitions: [],
    defaultPreset: DEFAULT_PRESET,
    now: resolvedNow,
  });

  const [values, setValues] = useState<QueueFilterValues>(DEFAULTS);
  const variables = useMemo(() => ({ filter: buildInquiryHubFilter(filters.range, values) }), [filters.range, values]);
  const subscriptionParams = useMemo(() => inquiryHubSubscriptionParams(today), [today]);

  const { data, loading, error, cohortTruncated } = useLiveGraphQL(ALL_INQUIRY_PATIENTS_QUERY, variables, {
    enablePagination: true,
    pageSize: QUEUE_PAGE_SIZE,
    autoLoadUpTo: COHORT_ROW_CAP,
    subscriptionParams,
  });
  const cohortTotal = useCohortTotal('Patient', cohortTruncated ? variables.filter : undefined);

  useEffect(() => {
    if (error && isMedplumRateLimitError(error)) {
      alert.warning({
        ...getRateLimitAlert(
          `The system temporarily limited how fast we can load the inquiry queue.
          Wait a few seconds before trying again.`
        ),
      });
    }
  }, [error]);

  const patients = useMemo(() => (error ? [] : ((data ?? []) as InquiryPatientResult[])), [data, error]);

  const patientRefs = useMemo(
    () =>
      patients
        .filter((patient) => patient.id)
        .map((patient) => getReferenceString(patient))
        .filter((reference): reference is string => reference !== undefined),
    [patients]
  );

  const datedPatients = useMemo(
    () =>
      patients
        .filter((patient) => patient.id && readPatientCreatedAt(patient))
        .map((patient) => getReferenceString(patient))
        .filter((reference): reference is string => reference !== undefined),
    [patients]
  );

  const hydration = usePacQueueHydration(
    patientRefs,
    {
      ...HYDRATION_NEEDS,
      lastCommunicationSince: filters.range.from ?? lastCommunicationFloor(today),
      datedPatients,
    },
    `intake-inquiry:${variables.filter}`
  );

  const builtRows = useMemo(
    () => buildInquiryRows(patients, hydration, resolvedNow, { signedUpFrom: filters.range.from }),
    [patients, hydration, resolvedNow, filters.range.from]
  );
  const edits = useHubRowEdits(builtRows, values.status);
  const { rows } = edits;

  const setFilter = (key: QueueFilterKey) => (value: string) => setValues((prev) => ({ ...prev, [key]: value }));
  // The server filters on the status tag; this drops a row whose status was just changed here.
  // Rows that arrive by subscription skip the query's filter, so PN, track and location
  // are checked on their tags too. Status reads the row, so a change made here applies at once.
  const visibleRows = useMemo(
    () =>
      rows.filter(
        (row) =>
          matchesPatientFilterTags(row.patient, { ...values, status: QUEUE_FILTER_ANY }) &&
          (values.status === QUEUE_FILTER_ANY || row.activeLead.status === values.status)
      ),
    [rows, values]
  );
  const { options: pnOptions } = usePatientNavigatorOptions();
  const { markets } = useMarketOptions();
  const locationOptions = useMemo(() => optionsFromRows(markets, 'All'), [markets]);

  const { onNoteSaved, changeStatus, undoStatus } = edits;
  const columns = useMemo(
    () => [
      patientColumn<HubInquiryRow>(),
      paymentTrackColumn<HubInquiryRow>(),
      pnColumn<HubInquiryRow>(),
      signedUpColumn<HubInquiryRow>(),
      lastContactColumn<HubInquiryRow>(),
      notesColumn<HubInquiryRow>({ onSaved: onNoteSaved }),
      statusColumn<HubInquiryRow>({ onChange: changeStatus, onUndo: undoStatus }),
    ],
    [onNoteSaved, changeStatus, undoStatus]
  );

  return (
    <Box style={TAB_STYLE}>
      <QueueFilterBar
        date={
          <ListRangeControl
            label="Signed up"
            presets={SIGNED_UP_PRESETS}
            presetId={filters.presetId}
            value={filters.range}
            onChange={filters.setRange}
          />
        }
        filters={[
          { label: 'PN', value: values.pn, options: pnOptions, onChange: setFilter('pn') },
          { label: 'Payment track', value: values.track, options: TRACK_FILTER_OPTIONS, onChange: setFilter('track') },
          { label: 'Location', value: values.location, options: locationOptions, onChange: setFilter('location') },
          { label: 'Status', value: values.status, options: LEAD_STATUS_FILTER_OPTIONS, onChange: setFilter('status') },
        ]}
        isFiltered={isQueueFiltered(values, DEFAULTS) || filters.presetId !== DEFAULT_PRESET}
        onReset={() => {
          setValues(DEFAULTS);
          filters.setRange(DEFAULT_PRESET);
        }}
      />
      {shouldWarnTruncated(cohortTruncated, rows.length, cohortTotal) && (
        <CohortTruncationNotice loaded={rows.length} total={cohortTotal} />
      )}
      <QueueTable
        columns={columns}
        data={visibleRows}
        loading={loading || hydration.loading}
        initialSorting={[{ id: 'signedUp', desc: true }]}
        onOpenPatient={(patientId) => navigate(`/Patient/${patientId}/timeline`)}
        getRowClassName={edits.rowClassName}
      />
      <QueueFooter shown={visibleRows.length} />
    </Box>
  );
}
